import { lazy, Suspense } from 'react';
import { YearProvider } from './lib/yearContext';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth, type User } from './lib/auth';
import { PAGES, type PageKey } from './lib/pages';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import LoadingSpinner from './components/LoadingSpinner';

const DashboardPage    = lazy(() => import('./pages/DashboardPage'));
const EventsPage       = lazy(() => import('./pages/EventsPage'));
const InstitutePage    = lazy(() => import('./pages/InstitutePage'));
const TimelinePage     = lazy(() => import('./pages/TimelinePage'));
const AuditPage        = lazy(() => import('./pages/AuditPage'));
const UsersPage        = lazy(() => import('./pages/UsersPage'));
const ManageEventsPage = lazy(() => import('./pages/ManageEventsPage'));
const SomatoriaPage    = lazy(() => import('./pages/SomatoriaPage'));
const FxRatesPage      = lazy(() => import('./pages/FxRatesPage'));
const BudgetsPage      = lazy(() => import('./pages/BudgetsPage'));

// Primeira página permitida do usuário (ordem do registro). Fallback: /login.
function firstAllowedRoute(user: User): string {
  const match = PAGES.find((p) => user.allowedPages.includes(p.key));
  return match ? match.route : '/login';
}

// Usuário autenticado — qualquer role (inclusive null/pending)
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

// Guard por página: bloqueia se a página não estiver nas páginas efetivas do usuário.
function PageRoute({ page, children }: { page: PageKey; children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.allowedPages.includes(page)) return <Navigate to={firstAllowedRoute(user)} replace />;
  return <>{children}</>;
}

const wrap = (el: React.ReactNode) => <Suspense fallback={<LoadingSpinner />}>{el}</Suspense>;

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <YearProvider><Layout /></YearProvider>
          </ProtectedRoute>
        }
      >
        <Route index            element={<PageRoute page="dashboard">{wrap(<DashboardPage />)}</PageRoute>} />
        <Route path="events"    element={<PageRoute page="events">{wrap(<EventsPage />)}</PageRoute>} />
        <Route path="institute" element={<PageRoute page="institute">{wrap(<InstitutePage />)}</PageRoute>} />
        <Route path="timeline"  element={<PageRoute page="timeline">{wrap(<TimelinePage />)}</PageRoute>} />
        <Route path="somatoria" element={<PageRoute page="somatoria">{wrap(<SomatoriaPage />)}</PageRoute>} />
        <Route path="manage-events" element={<PageRoute page="manage-events">{wrap(<ManageEventsPage />)}</PageRoute>} />
        <Route path="budgets"   element={<PageRoute page="budgets">{wrap(<BudgetsPage />)}</PageRoute>} />
        <Route path="users"     element={<PageRoute page="users">{wrap(<UsersPage />)}</PageRoute>} />
        <Route path="audit"     element={<PageRoute page="audit">{wrap(<AuditPage />)}</PageRoute>} />
        <Route path="fx-rates"  element={<PageRoute page="fx-rates">{wrap(<FxRatesPage />)}</PageRoute>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
